import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import goblinAvatar from '../assets/icon/goblin_avatar_2m65vv2m65vv2m65.webp';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../api/client';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context';
import Turnstile, { TURNSTILE_SITE_KEY } from './Turnstile';

// ── Country helpers ──────────────────────────────────────────────────────────
export function countryDisplay(codeOrName, lang = 'en') {
  if (!codeOrName) return '';
  if (codeOrName.length <= 3) {
    try { return new Intl.DisplayNames([lang, 'en'], { type: 'region' }).of(codeOrName.toUpperCase()) || codeOrName; }
    catch { return codeOrName; }
  }
  return codeOrName;
}

export function CountrySelect({ value, onChange, className = '', style = {}, placeholder = '— Šalis —' }) {
  const { i18n } = useTranslation();
  const [countries, setCountries] = useState([]);
  useEffect(() => {
    api.get('/countries').then(r => setCountries(r.data || [])).catch(() => {});
  }, []);
  const sorted = [...countries].sort((a, b) => {
    const na = countryDisplay(a.code, i18n.language);
    const nb = countryDisplay(b.code, i18n.language);
    return na.localeCompare(nb);
  });
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)} className={className} style={style}>
      <option value="">{placeholder}</option>
      {sorted.map(c => (
        <option key={c.code} value={c.code}>{countryDisplay(c.code, i18n.language)}</option>
      ))}
    </select>
  );
}

// ── WYSIWYG Editor (react-quill dark theme) ─────────────────────────────────
const QUILL_TOOLBAR = [
  ['bold', 'italic', 'underline', 'strike'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['blockquote'],
  [{ header: [1, 2, 3, false] }],
  ['clean'],
];
const QUILL_FORMATS = ['bold','italic','underline','strike','list','bullet','blockquote','header'];

export function WysiwygEditor({ value, onChange, placeholder = '', minHeight = 180 }) {
  const modules = useMemo(() => ({ toolbar: QUILL_TOOLBAR }), []);
  return (
    <div className="wysiwyg-wrap" style={{ '--wysiwyg-min-h': `${minHeight}px` }}>
      <ReactQuill
        theme="snow"
        value={value || ''}
        onChange={onChange}
        modules={modules}
        formats={QUILL_FORMATS}
        placeholder={placeholder}
      />
    </div>
  );
}

// Allowlist-based HTML sanitizer using the browser's own parser (not regex).
// Defense-in-depth: the backend already sanitizes on write, this guards the sink.
const ALLOWED_TAGS = new Set([
  'B', 'STRONG', 'I', 'EM', 'U', 'S', 'P', 'BR', 'UL', 'OL', 'LI',
  'H1', 'H2', 'H3', 'BLOCKQUOTE', 'SPAN', 'A',
]);
const ALLOWED_ATTRS = { A: new Set(['href', 'target', 'rel']) };
const SAFE_URL = /^(https?:|mailto:|\/|#)/i;

function sanitizeHtml(dirty) {
  if (typeof window === 'undefined' || !window.DOMParser) return '';
  const doc = new DOMParser().parseFromString(String(dirty), 'text/html');
  const walk = (node) => {
    // Iterate over a static copy — we mutate children as we go.
    [...node.childNodes].forEach((child) => {
      if (child.nodeType === 1) { // element
        const tag = child.tagName;
        if (!ALLOWED_TAGS.has(tag)) {
          child.replaceWith(...child.childNodes); // unwrap unknown tags, keep text
          return;
        }
        const allowed = ALLOWED_ATTRS[tag] || new Set();
        [...child.attributes].forEach((attr) => {
          const name = attr.name.toLowerCase();
          if (!allowed.has(name)) { child.removeAttribute(attr.name); return; }
          if ((name === 'href' || name === 'src') && !SAFE_URL.test(attr.value.trim())) {
            child.removeAttribute(attr.name);
          }
        });
        if (tag === 'A') { // harden external links
          child.setAttribute('rel', 'noopener noreferrer nofollow');
        }
        walk(child);
      } else if (child.nodeType === 8) { // comment
        child.remove();
      }
    });
  };
  walk(doc.body);
  return doc.body.innerHTML;
}

// Helper: renders HTML or plain text safely
export function RichDisplay({ html, className }) {
  if (!html) return null;
  const str = String(html);
  if (/<[a-z][\s\S]*>/i.test(str)) {
    const clean = sanitizeHtml(str);
    return <div className={`rich-display${className ? ' ' + className : ''}`} dangerouslySetInnerHTML={{ __html: clean }} />;
  }
  return (
    <div className={`rich-display${className ? ' ' + className : ''}`}>
      {str.split(/\n\n+/).map((para, i) => (
        <p key={i} style={{ marginBottom: '0.9em', marginTop: 0 }}>
          {para.split('\n').map((line, j, arr) => (
            <span key={j}>{line}{j < arr.length - 1 && <br />}</span>
          ))}
        </p>
      ))}
    </div>
  );
}

// ── Sidebar ────────────────────────────────────────────
export function Sidebar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const mainLinks = [
    { path: '/clubs', icon: '🛡️', label: t('nav.clubs') },
    { path: '/events', icon: '📅', label: t('nav.events'), count: 5 },
    { path: '/larp', icon: '⚔️', label: t('nav.larp') },
    { path: '/calendar', icon: '🗓️', label: t('nav.calendar') },
    { path: '/news', icon: '📰', label: t('nav.news') },
    { path: '/locations', icon: '📍', label: t('nav.locations') },
  ];

  const categories = ['RPG', 'LARP', 'CCG', 'Warhammer', 'Cosplay', 'Boardgames', 'Tabletop'];
  const cities = ['Vilnius', 'Kaunas', 'Klaipėda', 'Šiauliai', 'Panevėžys', 'Online'];

  return (
    <aside className="sidebar">
      <div className="sidebar-section">
        <div className="sidebar-section-title">🗺️ Naršyti</div>
        {mainLinks.map(({ path, icon, label, count }) => (
          <button
            key={path}
            className={`sidebar-item ${location.pathname === path ? 'active' : ''}`}
            onClick={() => navigate(path)}
          >
            <span>{icon}</span>
            {label}
            {count && <span className="sidebar-count">{count}</span>}
          </button>
        ))}
      </div>

      <div className="sidebar-section">
        <div className="sidebar-section-title">⚔️ Kategorijos</div>
        {categories.map(cat => (
          <button key={cat} className="sidebar-item">
            <span className="sidebar-arrow">›</span>
            {cat}
          </button>
        ))}
      </div>

      <div className="sidebar-section">
        <div className="sidebar-section-title">📍 Miestai</div>
        {cities.map(city => (
          <button key={city} className="sidebar-item">
            <span className="sidebar-arrow">›</span>
            {city}
          </button>
        ))}
      </div>
    </aside>
  );
}

// ── AdminBar ───────────────────────────────────────────
export function AdminBar() {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();

  if (!isAdmin) return null;

  return (
    <div className="admin-bar">
      <span className="admin-bar-label">ADMIN:</span>
      <button className="admin-action action-approve" onClick={() => alert('✓ Patvirtinta!')}>{t('admin.approve')}</button>
      <button className="admin-action action-reject" onClick={() => alert('✗ Atmesta!')}>{t('admin.reject')}</button>
      <button className="admin-action action-spam" onClick={() => alert('⚠ Pažymėta kaip šlamštas!')}>{t('admin.spam')}</button>
      <button className="admin-action action-pending">{t('admin.pending')}</button>
      <span className="admin-bar-hint">— Pasirinkite įrašą ir naudokite veiksmus</span>
      <button className="admin-panel-btn" onClick={() => alert('Eiti į admin panelę...')}>
        ⚙️ Admin Panel →
      </button>
    </div>
  );
}

// ── CookieConsent ──────────────────────────────────────
export function CookieConsent() {
  const { t } = useTranslation();
  const saved = localStorage.getItem('fantastai_cookie');
  const [popup,    setPopup]    = useState(!saved);
  const [settings, setSettings] = useState(false);
  const [prefs,    setPrefs]    = useState({ analytics: true, marketing: false });

  const save = (level) => {
    localStorage.setItem('fantastai_cookie', level);
    setPopup(false);
    setSettings(false);
  };

  const togglePref = (k) => setPrefs(p => ({ ...p, [k]: !p[k] }));

  return (
    <>
      {/* Bottom-left popup panel */}
      {popup && (
        <div style={{ position:'fixed', bottom:20, left:20, zIndex:9000, width:320, background:'#0d0a18', border:'1px solid rgba(240,192,64,0.25)', borderRadius:16, padding:'22px 22px 18px', boxShadow:'0 16px 60px rgba(0,0,0,0.8)', animation:'slideUpIn .25s ease' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
            <span style={{ fontSize:20 }}>🍪</span>
            <span style={{ fontFamily:'Cinzel,serif', fontSize:14, color:'#f0c040', fontWeight:700 }}>Slapukų naudojimas</span>
          </div>
          <p style={{ fontSize:12, color:'rgba(200,180,255,0.6)', lineHeight:1.65, marginBottom:16 }}>
            Naudojame slapukus jūsų patirčiai gerinti. Galite pasirinkti, kuriuos priimti.
          </p>
          {settings ? (
            <div style={{ marginBottom:14 }}>
              {[['essential','Būtinieji','Privalomi svetainės veikimui',true],
                ['analytics','Analitika','Padeda suprasti naudojimą',false],
                ['marketing','Rinkodara','Reklaminiai slapukai',false]].map(([k,label,desc,forced])=>(
                <div key={k} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                  <div>
                    <div style={{ fontSize:12, color:'#ede8f8', fontWeight:600 }}>{label}</div>
                    <div style={{ fontSize:10, color:'rgba(200,180,255,0.35)' }}>{desc}</div>
                  </div>
                  <input type="checkbox" checked={forced || !!prefs[k]} disabled={forced} onChange={()=>!forced&&togglePref(k)}
                    style={{ width:16, height:16, cursor:forced?'default':'pointer', accentColor:'#f0c040' }} />
                </div>
              ))}
              <button style={{ marginTop:12, width:'100%', background:'rgba(240,192,64,0.12)', border:'1px solid rgba(240,192,64,0.28)', borderRadius:8, color:'#f0c040', padding:'9px', cursor:'pointer', fontSize:12, fontWeight:700 }}
                onClick={()=>save(prefs.marketing?'all':(prefs.analytics?'analytics':'essential'))}>
                Išsaugoti
              </button>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              <button style={{ background:'#f0c040', border:'none', borderRadius:8, color:'#050308', padding:'10px', cursor:'pointer', fontSize:13, fontWeight:700 }}
                onClick={()=>save('all')}>Priimti visus</button>
              <div style={{ display:'flex', gap:8 }}>
                <button style={{ flex:1, background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, color:'rgba(200,180,255,0.75)', padding:'9px 4px', cursor:'pointer', fontSize:11 }}
                  onClick={()=>save('essential')}>Tik būtinieji</button>
                <button style={{ flex:1, background:'none', border:'1px solid rgba(240,192,64,0.15)', borderRadius:8, color:'rgba(240,192,64,0.6)', fontSize:11, cursor:'pointer', padding:'9px 4px' }}
                  onClick={()=>setSettings(true)}>Tinkinti</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Cookie settings circle — bottom-left, above popup */}
      {!popup && (
        <button
          onClick={() => setPopup(true)}
          title="Slapukų nuostatos"
          style={{
            position:'fixed', bottom:120, left:16, zIndex:200,
            width:36, height:36, borderRadius:'50%',
            background:'rgba(10,6,24,0.92)', border:'1px solid rgba(240,192,64,0.28)',
            color:'rgba(200,180,255,0.5)', fontSize:16, cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center',
            boxShadow:'0 2px 12px rgba(0,0,0,0.6)',
            transition:'border-color .2s, color .2s, transform .15s',
          }}
          onMouseEnter={e=>{ e.currentTarget.style.borderColor='rgba(240,192,64,0.65)'; e.currentTarget.style.color='#f0c040'; e.currentTarget.style.transform='scale(1.1)'; }}
          onMouseLeave={e=>{ e.currentTarget.style.borderColor='rgba(240,192,64,0.28)'; e.currentTarget.style.color='rgba(200,180,255,0.5)'; e.currentTarget.style.transform='scale(1)'; }}
        >
          🍪
        </button>
      )}
    </>
  );
}

const GOBLIN_REPLY = (nick) =>
  `Ugh, ${nick}... Mes čiaaa valdom! Goblin ir Mazz Sveikazz sako — esame po begalybės valdžia ir greitai grįšim. Bet jei nori palikti žinutę — rašyk čia. Bozz žinos. 🐉`;

// ── ChatWidget ─────────────────────────────────────────
export function ChatWidget() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [infoForm, setInfoForm] = useState({ name: '', email: '', nickname: '' });
  const [infoErr, setInfoErr] = useState('');
  const [msg, setMsg] = useState('');
  const [history, setHistory] = useState([]);
  const msgEndRef = useRef(null);
  const [chatCaptcha, setChatCaptcha] = useState('');
  const chatCaptchaResetRef = useRef(null);
  const chatCaptchaOk = !TURNSTILE_SITE_KEY || !!chatCaptcha;

  useEffect(() => {
    if (msgEndRef.current) msgEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [history]);

  const submitInfo = useCallback(() => {
    if (!infoForm.name.trim() || !infoForm.email.trim() || !infoForm.nickname.trim()) {
      setInfoErr('Prašome užpildyti visus laukus.');
      return;
    }
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(infoForm.email)) {
      setInfoErr('Neteisingas el. pašto formatas.');
      return;
    }
    if (!chatCaptchaOk) {
      setInfoErr('Patvirtinkite, kad esate žmogus.');
      return;
    }
    const nick = infoForm.nickname.trim();
    setUserInfo(infoForm);
    setHistory([{
      type: 'bot',
      text: `Goblin sako sveikas, ${nick}! Mazz Sveikazz, Kurz gliu dėti? Mes čiaaa valdom. Kas ir bozz... Esame po begalybės valdžia ir greitai grįšim. 🐉 Jei nori palikti žinutę — rašyk!`,
    }]);
  }, [infoForm, chatCaptchaOk]);

  const send = useCallback(() => {
    if (!msg.trim() || !userInfo) return;
    const text = msg.trim();
    setMsg('');
    setHistory(h => [
      ...h,
      { type: 'user', text },
      { type: 'bot', text: GOBLIN_REPLY(userInfo.nickname) },
    ]);
  }, [msg, userInfo]);

  return (
    <div className="chat-fab">
      {open && (
        <div className="chat-window">
          <div className="chat-header">
            <div className="chat-avatar-wrap">
              <img src={goblinAvatar} alt="Goblin" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
            </div>
            <div className="chat-info-wrap">
              <div className="chat-info-name">{t('chat.title')}</div>
              <div className="chat-info-status">● {t('chat.online')}</div>
            </div>
            <button className="modal-close" onClick={() => setOpen(false)}>×</button>
          </div>

          {!userInfo ? (
            <div className="chat-register">
              <div className="chat-msg bot" style={{ margin: '10px 10px 0' }}>
                Goblin sako — kas tu esi? Prieš rašydamas, prisistatyk! Mazz Sveikazz nori žinoti. 👺
              </div>
              <div style={{ padding: '10px' }}>
                {infoErr && <div style={{ fontSize: 11, color: '#ff6060', marginBottom: 6 }}>{infoErr}</div>}
                <input className="chat-input" style={{ marginBottom: 6, width: '100%' }} placeholder="Vardas" value={infoForm.name} onChange={e => setInfoForm(f => ({ ...f, name: e.target.value }))} />
                <input className="chat-input" style={{ marginBottom: 6, width: '100%' }} placeholder="El. paštas" value={infoForm.email} onChange={e => setInfoForm(f => ({ ...f, email: e.target.value }))} />
                <input className="chat-input" style={{ marginBottom: 8, width: '100%' }} placeholder="Slapyvardis (nickname)" value={infoForm.nickname} onChange={e => setInfoForm(f => ({ ...f, nickname: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && submitInfo()} />
                <Turnstile onToken={setChatCaptcha} resetRef={chatCaptchaResetRef} style={{ margin: '0 0 8px' }} />
                <button className="chat-send-btn" style={{ width: '100%', height: 32, borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: chatCaptchaOk ? 'pointer' : 'not-allowed', opacity: chatCaptchaOk ? 1 : 0.6 }} onClick={submitInfo} disabled={!chatCaptchaOk}>
                  Įeiti į pokalbį ➤
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="chat-messages">
                {history.map((m, i) => (
                  <div key={i} className={`chat-msg ${m.type}`}>{m.text}</div>
                ))}
                <div ref={msgEndRef} />
              </div>
              <div className="chat-input-row">
                <input
                  className="chat-input"
                  placeholder={t('chat.placeholder')}
                  value={msg}
                  onChange={e => setMsg(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && send()}
                />
                <button className="chat-send-btn" onClick={send}>✈</button>
              </div>
            </>
          )}
        </div>
      )}
      <button className="chat-toggle-btn" onClick={() => setOpen(o => !o)}>
        {open ? '×' : '💬'}
      </button>
    </div>
  );
}

// ── Ad Box ─────────────────────────────────────────────
export function AdBox({ position = 'general' }) {
  const { t } = useTranslation();
  const [ad, setAd]       = useState(undefined); // undefined=loading, null=none
  const googleRef = useRef(null);

  useEffect(() => {
    api.get('/ads/active', { params: { position } })
      .then(r => setAd(r.data || null))
      .catch(() => setAd(null));
  }, [position]);

  useEffect(() => {
    if (ad?.type === 'google' && ad.google_code && googleRef.current) {
      googleRef.current.innerHTML = '';
      const range = document.createRange();
      const frag  = range.createContextualFragment(ad.google_code);
      googleRef.current.appendChild(frag);
    }
  }, [ad]);

  const handleClick = () => {
    if (ad?.id) {
      api.post(`/ads/${ad.id}/click`).catch(() => {});
    }
  };

  if (ad === undefined) return (
    <div className="ad-box" style={{ opacity: 0.4 }}>
      <div className="ad-label">{t('ad.label')}</div>
      <div className="ad-title" style={{ fontSize: 12, color: 'rgba(200,180,255,0.3)' }}>Kraunama...</div>
    </div>
  );

  if (ad === null) return (
    <div className="ad-box">
      <div className="ad-label">{t('ad.label')}</div>
      <div className="ad-title">{t('ad.cta')}</div>
      <div className="ad-sub">{t('ad.sub')}</div>
      <div className="ad-contact">→ {t('ad.contact')}</div>
    </div>
  );

  if (ad.type === 'google') return (
    <div className="ad-box" style={{ padding: 0, overflow: 'hidden', minHeight: 90 }}>
      <div ref={googleRef} style={{ width: '100%' }} />
    </div>
  );

  return (
    <a
      href={ad.link_url || '#'}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      style={{ display: 'block', textDecoration: 'none' }}
    >
      <div className="ad-box" style={{ padding: 0, overflow: 'hidden', cursor: 'pointer' }}>
        <div className="ad-label" style={{ padding: '6px 14px 0' }}>{t('ad.label')}</div>
        {ad.image_url ? (
          <img
            src={ad.image_url}
            alt={ad.name}
            style={{ width: '100%', display: 'block', maxHeight: 200, objectFit: 'cover' }}
          />
        ) : (
          <div style={{ padding: '8px 14px 14px' }}>
            <div className="ad-title">{ad.name}</div>
            <div className="ad-contact">→ {ad.link_url}</div>
          </div>
        )}
      </div>
    </a>
  );
}

